import { Link } from 'react-router'
import clsx from 'clsx'
import { BlogPostBanner } from '~/components/blog/blog-post-banner'
import { P } from '~/components-v2'
import { PublishDate } from '~/components/blog/publish-date'
import { personPhotoSrc } from '~/components/human'
import type { PostSummary } from '~/types'

export type VideoPost = {
  title: string
  href: string
  thumbnail: string | null
  isVideo: true
}

type PostCardProps = ((PostSummary & { isVideo?: boolean }) | VideoPost) & {
  loading?: 'lazy' | 'eager'
  cover?: boolean
  postBasePath?: '/articles' | '/blog'
  variant?: 'default' | 'carousel' | 'blog-grid'
}

export const PostCard = (props: PostCardProps) => {
  if ('href' in props) {
    return (
      <article className="post-card-container">
        <a
          className="post-card"
          href={props.href}
          rel="noopener noreferrer"
          target="_blank"
        >
          <div
            className="post-card__media"
            style={{ aspectRatio: 'var(--post-card-media-ratio, 16 / 9)' }}
          >
            {props.thumbnail && (
              <img
                alt=""
                loading="lazy"
                src={props.thumbnail}
                style={{
                  display: 'block',
                  height: '100%',
                  objectFit: 'cover',
                  width: '100%',
                }}
              />
            )}

            <span className="post-card__play" aria-hidden="true">
              <svg viewBox="0 0 35 42">
                <path d="M0 37.358V4.59838C0 1.0011 3.94702 -1.19684 7.00798 0.692852L32.6763 16.5393C35.5345 18.3037 35.5949 22.4386 32.7896 24.2859L7.12131 41.1888C4.06639 43.2005 0 41.0158 0 37.358Z" />
              </svg>
            </span>
          </div>

          <div className="content">
            <h3 className="heading-s">Watch: {props.title}</h3>
          </div>
        </a>
      </article>
    )
  }

  const {
    banner,
    cover,
    slug,
    authors,
    published,
    subtitle,
    title,
    description,
    loading,
    postBasePath = '/blog',
    variant = 'default',
    isVideo = false,
  } = props

  const [primaryAuthor] = authors
  const showRichByline = variant === 'carousel' || variant === 'blog-grid'

  const coverContent = cover && (
    <>
      <h4 className="heading-xxs">{subtitle}</h4>
      <p className="body-m">{description}</p>
    </>
  )

  return (
    <article className="post-card-container">
      <Link
        to={`${postBasePath}/${slug}`}
        className={clsx(
          'post-card',
          cover && 'post-card-cover',
          variant === 'blog-grid' && 'post-card--blog-grid',
        )}
      >
        {banner &&
          (isVideo ? (
            <div className="post-card__media">
              <BlogPostBanner loading={loading} {...banner} />
              <span className="post-card__play" aria-hidden="true">
                <svg viewBox="0 0 35 42">
                  <path d="M0 37.358V4.59838C0 1.0011 3.94702 -1.19684 7.00798 0.692852L32.6763 16.5393C35.5345 18.3037 35.5949 22.4386 32.7896 24.2859L7.12131 41.1888C4.06639 43.2005 0 41.0158 0 37.358Z" />
                </svg>
              </span>
            </div>
          ) : (
            <BlogPostBanner loading={loading} {...banner} />
          ))}

        <div className="content">
          <h3 className={cover ? 'heading-l' : 'heading-s'}>
            {isVideo && 'Watch: '}
            {title}
          </h3>

          {coverContent}

          {showRichByline ? (
            <div className="post-card-byline">
              {primaryAuthor?.photo && (
                <img
                  alt=""
                  className="post-card-author-image"
                  loading="lazy"
                  src={personPhotoSrc(primaryAuthor.photo, 32)}
                />
              )}

              <div className="post-card-meta">
                {primaryAuthor && (
                  <span className="post-card-author">{primaryAuthor.name}</span>
                )}
                <span className="post-card-date">
                  <PublishDate date={published} />
                </span>
              </div>
            </div>
          ) : (
            <P color="text4" fontSize="sm">
              <PublishDate date={published} />
              {!!authors.length && (
                <span className="authors">
                  {authors.map(({ name, slug }) => (
                    <span key={slug} className="author">
                      {name}
                    </span>
                  ))}
                </span>
              )}
            </P>
          )}
        </div>
      </Link>
    </article>
  )
}
